import { useState, type ReactNode } from 'react';
import Avatar from '@mui/material/Avatar';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@eis/core';
import { brandAssets, radius } from '@eis/theme';
import { TenantBadge } from './TenantBadge';
import { useChromeVariant } from './useChromeVariant';

/** "Dana Neumann" → "DN"; a single-word name keeps two letters rather than one. */
function initialsOf(displayName: string): string {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return (words[0] ?? '').slice(0, 2).toUpperCase();
  return `${words[0]?.[0] ?? ''}${words.at(-1)?.[0] ?? ''}`.toUpperCase();
}

/**
 * Berry's profile chip: one pill in the top-right carrying who you are and which tenant you are
 * acting for, with the account menu behind it.
 *
 * Tenant and identity belong in the same object because they answer the same question, and it is
 * the question support asks first when a screenshot arrives. Only the identity half is a button —
 * the tenant badge inside it is information, and wrapping it in the menu trigger would give the
 * button an accessible name that reads "Dana Neumann tenant-a POOL".
 */
export function ProfileChip(): ReactNode {
  const { t } = useTranslation('shell');
  const { user, tenant, signOut } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const suite = useChromeVariant().variant === 'suite';

  const displayName = user?.displayName ?? '—';

  return (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{
        alignItems: 'center',
        // The chip states who you are and which tenant you act for. Squeezed, it says neither:
        // in a flex toolbar it would shrink until the tenant name wrapped a character per line.
        flexShrink: 0,
        border: 1,
        borderColor: 'divider',
        borderRadius: `${String(radius.pill)}px`,
        bgcolor: 'background.paper',
        pl: 1.25,
        pr: 0.5,
        py: 0.25,
      }}
    >
      {tenant !== null && (
        <>
          <TenantBadge />
          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
        </>
      )}
      <ButtonBase
        onClick={(event) => {
          setAnchor(event.currentTarget);
        }}
        aria-label={t('topbar.account')}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        sx={{
          gap: 0.5,
          px: 0.5,
          py: 0.25,
          borderRadius: `${String(radius.pill)}px`,
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
        }}
      >
        <Avatar
          // Decorative: the button already carries the account label, and the initials would
          // otherwise be announced as a second, meaningless name.
          aria-hidden
          // The suite shows the legacy's default avatar image (icon-map.json `header.profile`);
          // Berry keeps its initials on the primary colour.
          src={suite ? brandAssets.defaultAvatar : undefined}
          sx={{
            width: suite ? 30 : 26,
            height: suite ? 30 : 26,
            fontSize: '0.6875rem',
            bgcolor: suite ? 'transparent' : 'primary.main',
          }}
        >
          {suite ? null : initialsOf(displayName)}
        </Avatar>
        <Typography
          variant="body2"
          // A long name is TRUNCATED, never folded: "Product tenant-a" down the side of the bar
          // is unreadable, and an ellipsis at least says there is more.
          sx={{
            fontWeight: 600,
            maxWidth: { xs: 96, sm: 160 },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          noWrap
        >
          {displayName}
        </Typography>
        <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => {
          setAnchor(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disabled>
          <Stack>
            <Typography variant="body2">{displayName}</Typography>
            {user !== null && (
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
            )}
          </Stack>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            void signOut();
          }}
        >
          {t('topbar.signOut')}
        </MenuItem>
      </Menu>
    </Stack>
  );
}
